import {
  ArticleStatus,
  IArticle,
  IAudit,
  ICrossTherapyArticleLine,
  IDoctor,
  IIntegratedCare,
  IIntegratedCareReturnDelivery,
  IMedicalOrderRequest,
  IPatient,
  IPlannedOrderedArticleLine,
  IPrescriptionRequest,
  ISingleOrder,
  ISwodocArticleData,
  ITemplate,
  IntegratedCareDocumentEntity,
  Therapy,
  therapyItems,
} from '@alberta/konexi-shared';
import { Injectable } from '@angular/core';
import { cloneDeep, isEmpty } from 'lodash';
import { ServiceOrderViewModel } from 'src/app/business/service-order-entity/view-model/service-order-view-model';
import { ArticleDB, DoctorDB, IntegratedCareDB } from 'src/app/common/repository/databases';

import { getTherapy } from '@alberta/konexi-shared';
import { SwodocArticle } from 'src/app/shared/components/document/document-edit/nodes/add-article-element/swodoc-node-add-article-element';
import { BodyPart } from 'src/app/shared/models/body-part.model';
import { DocumentEntityType } from 'src/app/shared/models/swodoc/document-entity-type';
import { AuditService } from 'src/app/shared/services/audit/audit.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { BodyPartService } from 'src/app/shared/services/body-part.service';
import { CrossTherapyDocumentService } from 'src/app/shared/services/cross-therapy-document-service';
import { InstitutionAttributeService } from 'src/app/shared/services/institution-attribute.service';
import { QueryService } from 'src/app/shared/services/query/query.service';
import { SingleOrderedArticleLineViewModel } from '../../business/single-order/single-ordered-articleline-view-model';

// tslint:disable-next-line:max-line-length
export interface IPrepareAuditMetaDataInterface {
  patient: IPatient;
  selectedTherapy: Therapy;
  selectedTherapyType: number;
  selectedWoundLocation: BodyPart;
  additionalInfo: string;
}

@Injectable({ providedIn: 'root' })
export class SwodocCreateService {
  constructor(
    private _auditService: AuditService,
    private _auth: AuthService,
    private _crossTherapyDocumentService: CrossTherapyDocumentService,
    private _queryService: QueryService,
    private _institutionAttributeService: InstitutionAttributeService,
    private _bodyPartService: BodyPartService
  ) {}

  public therapyTypeRequired(therapyId: Therapy): boolean {
    const therapy = getTherapy(therapyId);
    return therapy.therapyTypes && therapy.therapyTypes.length > 0;
  }

  public async sortAndFilterTemplates(
    templates: ITemplate[],
    selectedTherapy?: number,
    selectedTherapyType?: number,
    patient?: IPatient,
    entityType?: DocumentEntityType,
    searchValue?: string
  ): Promise<ITemplate[]> {
    const userGroups: string[] = (await this._auth.getCurrentUserGroups()) || [];
    const therapyTypeRequired = this.therapyTypeRequired(selectedTherapy);
    const sortPayerFct = (a: ITemplate, b: ITemplate) => {
      return a.payer === b.payer ? 0 : a.payer ? -1 : 1;
    };
    const sortTherapyFct = (a: ITemplate, b: ITemplate) => {
      return a.therapyId === b.therapyId ? 0 : a.therapyId ? -1 : 1;
    };
    const sortTitleFct = (a: ITemplate, b: ITemplate) => {
      if (a.title > b.title) {
        return 1;
      }
      if (a.title < b.title) {
        return -1;
      }
      return 0;
    };
    const sortPatientEntityFct = (a: ITemplate, b: ITemplate) => {
      return a.patientEntity === b.patientEntity ? 0 : a.patientEntity ? -1 : 1;
    };
    const filterTherapyFct = (f: ITemplate) => {
      // Step 1:
      // show general documents when no therapy is selected
      if (!selectedTherapy && selectedTherapy !== 0 && !f.therapyId && f.therapyId !== 0) {
        return true;
      }
      // Step 2:
      // show no document when therapy is selected but no therapyType and TherapyType is need
      if (
        (selectedTherapy || selectedTherapy === 0) &&
        !selectedTherapyType &&
        selectedTherapyType !== 0 &&
        therapyTypeRequired
      ) {
        return false;
      }
      // Step 3:
      // filter after therapy and therapyType
      let returnValue = false;
      // show general documents
      if (!f.therapyId && f.therapyId !== 0) {
        return true;
      }
      if (selectedTherapy || selectedTherapy === 0) {
        // Check if the selected therapy matches the template's therapyId OR
        // if it's included in the template's additionalTherapies array
        const isMainTherapyMatch = f.therapyId === selectedTherapy;
        const isAdditionalTherapyMatch =
          Array.isArray(f.additionalTherapies) &&
          f.additionalTherapies.some(therapy => {
            // Convert the string enum value to the numeric enum value for comparison
            return Therapy[therapy as keyof typeof Therapy] === selectedTherapy;
          });

        returnValue = isMainTherapyMatch || isAdditionalTherapyMatch;

        // If the templates additionalTherapy matches, skip the therapyType filter
        if (isAdditionalTherapyMatch && !isMainTherapyMatch) {
          return true;
        }
      }
      if (!f.therapyTypeId && f.therapyTypeId !== 0) {
        return returnValue;
      }
      if (!selectedTherapyType && selectedTherapyType !== 0) {
        return false;
      }
      if (selectedTherapyType || selectedTherapyType === 0) {
        returnValue = returnValue && f.therapyTypeId === selectedTherapyType;
      }
      return returnValue;
    };
    const canSeeDrafts = await this._auth.userHasPermission('templateDraft', 8);
    const filterDraftFct = (template: ITemplate) => {
      if (!canSeeDrafts) {
        return !template.draft;
      }
      return true;
    };
    const filterPayerFct = (template: ITemplate) => {
      if (!patient) {
        return true;
      }
      if (!patient.payer || isEmpty(patient.payer)) {
        return !template.payer || template.payer.length === 0;
      }
      if (patient.payer && !isEmpty(patient.payer)) {
        return !template.payer || template.payer.length === 0 || template.payer.includes(patient.payer.payerId);
      }
      return true;
    };
    const filterArchivedFct = (template: ITemplate) => {
      return !template.archived;
    };
    const filterInstitutionDocuments = (template: ITemplate) => {
      return (
        Array.isArray(template.entityTypes) &&
        template.entityTypes.length === 1 &&
        template.entityTypes[0] === (entityType || 'patient')
      );
    };
    const filterPatientEntityFct = (template: ITemplate) => {
      if (!patient) {
        return true;
      }
      if (!patient.resmedTenant || isEmpty(patient.resmedTenant)) {
        return !template.patientEntity;
      }
      if (patient.resmedTenant && !isEmpty(patient.resmedTenant)) {
        return !template.patientEntity || template.patientEntity === patient.resmedTenant;
      }
      return true;
    };
    const filterByName = (template: ITemplate) => {
      if (!searchValue) {
        return true;
      } else {
        return template.title?.toLowerCase().includes(searchValue.toLowerCase());
      }
    };
    const filterGroupIdsAllowedToCreateFct = (template: ITemplate) => {
      const groupIdsAllowedToCreate = template?.groupIdsAllowedToCreate;
      if (!Array.isArray(groupIdsAllowedToCreate) || groupIdsAllowedToCreate?.length === 0) {
        return true;
      }
      if (!Array.isArray(userGroups) || userGroups.length === 0) {
        return false;
      }
      return groupIdsAllowedToCreate.some(groupId => userGroups.includes(groupId));
    };
    const sortedAndFilteredTemplates = templates
      .filter(filterByName)
      .filter(filterInstitutionDocuments)
      .filter(filterArchivedFct)
      .filter(filterDraftFct)
      .filter(filterGroupIdsAllowedToCreateFct)
      .filter(filterPayerFct)
      .filter(filterTherapyFct)
      .filter(filterPatientEntityFct)
      .sort(sortTitleFct)
      .sort(sortTherapyFct)
      .sort(sortPayerFct)
      .sort(sortPatientEntityFct);
    return sortedAndFilteredTemplates;
  }

  public async prepareCrossTherapyAudit(
    template: ITemplate,
    patient: IPatient,
    integratedCares: IIntegratedCare[]
  ): Promise<IAudit> {
    const crossTherapyArticleLines = await this._crossTherapyDocumentService.enhanceArticles(
      integratedCares,
      this._queryService
    );

    const prefillData = await this.getCrossTherapyPrefillData(
      template,
      patient,
      crossTherapyArticleLines,
      integratedCares
    );

    return this.getAudit(template, patient, prefillData);
  }

  public async prepareReturnDeliveryAudit(
    template: ITemplate,
    patient: IPatient,
    returnDelivery: IIntegratedCareReturnDelivery,
    articleData: ISwodocArticleData[]
  ): Promise<IAudit> {
    const prefillData =
      (await this._auditService.getPrefillData(
        template._id,
        patient._id,
        null,
        await this._auth.getCurrentUserId(),
        returnDelivery
      )) || [];
    // for the article node in the document we need to load the article data
    if (articleData?.length > 0) {
      prefillData.push({ _id: 'articleData', value: articleData });
    }
    const audit = this.getAudit(template, patient, prefillData, IntegratedCareDocumentEntity.returnDelivery);
    return audit;
  }

  public async preparePlannedOrderAudit(
    template: ITemplate,
    patient: IPatient,
    integratedCares: IIntegratedCare[],
    articleLines: IPlannedOrderedArticleLine[]
  ) {
    const crossTherapyArticleLines = await this._crossTherapyDocumentService.plannedOrderArticleLinesToCrossTherapy(
      articleLines,
      this._queryService
    );
    const prefillData = await this.getCrossTherapyPrefillData(
      template,
      patient,
      crossTherapyArticleLines,
      integratedCares
    );
    return this.getAudit(template, patient, prefillData);
  }

  private async getCrossTherapyPrefillData(
    template: ITemplate,
    patient: IPatient,
    crossTherapyArticleLines: ICrossTherapyArticleLine[],
    integratedCares: IIntegratedCare[]
  ) {
    const prefillData =
      (await this._auditService.getPrefillData(template._id, patient._id, null, await this._auth.getCurrentUserId())) ||
      [];
    prefillData.push({
      _id: 'crossTherapyArticleData',
      value: crossTherapyArticleLines,
      integratedCares: integratedCares,
    });
    return prefillData;
  }

  private async getAudit(
    template: ITemplate,
    patient: IPatient,
    prefillData: any,
    entityType?: IntegratedCareDocumentEntity
  ): Promise<IAudit> {
    const audit = await this._auditService.createAudit(template, prefillData);
    audit.patientId = patient._id;
    audit.regionId = patient.regionId;
    if (entityType) {
      audit.entityType = entityType;
    } else {
      audit.entityType = IntegratedCareDocumentEntity.crossTherapy;
    }
    if (template.type > -1) {
      audit.type = template.type;
    }
    return audit;
  }

  public async preparePatientAudit(template: ITemplate, metaData: IPrepareAuditMetaDataInterface): Promise<IAudit> {
    const _template = cloneDeep(template);
    const prefillData = await this._auditService.getPrefillData(
      _template._id,
      metaData.patient._id,
      metaData,
      await this._auth.getCurrentUserId()
    );
    const audit = await this.getTherapySpecificAudit(template, metaData, prefillData);
    return audit;
  }

  private async getTherapySpecificAudit(
    template: ITemplate,
    metaData: IPrepareAuditMetaDataInterface,
    prefillData: any
  ): Promise<IAudit> {
    const audit = await this._auditService.createPatientAudit(
      template,
      metaData.patient._id,
      metaData.patient.regionId,
      prefillData
    );
    if (template.type > -1) {
      audit.type = template.type;
    }
    if (metaData.selectedTherapy || metaData.selectedTherapy === 0) {
      audit.therapyId = metaData.selectedTherapy;
    } else if (template.therapyId || template.therapyId === 0) {
      audit.therapyId = template.therapyId;
    }
    if (metaData.selectedTherapyType || metaData.selectedTherapyType === 0) {
      audit.therapyTypeId = metaData.selectedTherapyType;
    } else if (template.therapyTypeId || template.therapyTypeId === 0) {
      audit.therapyTypeId = template.therapyTypeId;
    }
    if (metaData.selectedWoundLocation) {
      audit.woundLocation = metaData.selectedWoundLocation.areaId.toString();
    }
    if (metaData.additionalInfo) {
      audit.additionalInfo = metaData.additionalInfo;
    }
    return audit;
  }

  public async prepareMedicalOrderAudit(
    template: ITemplate,
    { patient, selectedServiceOrders }: { patient: IPatient; selectedServiceOrders: ServiceOrderViewModel[] },
    previousMedicalOrderRequest?: IMedicalOrderRequest
  ): Promise<IAudit> {
    const _template = cloneDeep(template);
    let metaData: IPrepareAuditMetaDataInterface;
    // services can have multiple locations, so we need to override the wound location text
    let overrideWoundLocationText;
    if (selectedServiceOrders.length === 1) {
      let bodyPart: BodyPart;
      if (selectedServiceOrders[0].additionalLocations?.length > 0) {
        overrideWoundLocationText = selectedServiceOrders[0].additionalLocations.map(loc => loc).join(', ');
      }
      if (selectedServiceOrders[0].woundLocation) {
        bodyPart = this._bodyPartService.bodyParts.find(bp => bp.areaId === selectedServiceOrders[0].woundLocation);
      }
      metaData = {
        selectedTherapy: selectedServiceOrders[0].therapyId,
        selectedTherapyType: selectedServiceOrders[0].therapyTypeId,
        selectedWoundLocation: bodyPart,
        patient: null,
        additionalInfo: null,
      };
    }

    const swodocPrefillData = await this._auditService.getPrefillData(
      _template._id,
      patient._id,
      metaData,
      await this._auth.getCurrentUserId(),
      null,
      overrideWoundLocationText
    );
    const prefillData = swodocPrefillData || [];

    // tslint:disable-next-line: no-shadowed-variable
    for (const { index: serviceOrderIndex, serviceOrder } of selectedServiceOrders.map((serviceOrder, index) => ({
      index,
      serviceOrder,
    }))) {
      const integratedCare = await this._queryService.get<IIntegratedCare>(
        serviceOrder.integratedCareId,
        IntegratedCareDB
      );
      const doctor = await this._queryService.get<IDoctor>(integratedCare.doctorId, DoctorDB);
      const { diagnose } = integratedCare;

      let correspondenceType;
      let to;
      if (doctor) {
        correspondenceType = await this._institutionAttributeService.getCorrespondenceTypeForInstitutionId(
          integratedCare.doctorId
        );
        to =
          correspondenceType === 'Fax'
            ? (doctor.faxPrescriptionRequest ?? doctor.fax)
            : (doctor.emailPrescriptionRequest ?? doctor.email);
      }
      const startDate = serviceOrder.startDate;
      const endDate = serviceOrder.endDate;

      const serviceLines = [...serviceOrder.orderedServiceLines];
      const notAvailableServiceLines = [];

      const medicalOrderData = {
        therapyId: integratedCare.therapyId,
        therapyTypeId: integratedCare.therapyTypeId,
        doctorId: integratedCare.doctorId,
        integratedCareId: serviceOrder.integratedCareId,
        correspondenceType,
        serviceLines,
        notAvailableServiceLines,
        serviceOrderId: serviceOrder._id,
        templateId: _template._id,
        diagnose,
        to,
        startDate,
        endDate,
        patientId: patient._id,
        payerId: patient.payer?.payerId,
        numberOfInsured: patient.payer?.numberOfInsured,
      };
      prefillData.push({
        _id: `medicalOrderData${serviceOrderIndex + 1}`,
        value: medicalOrderData,
      });
      let therapyAbbreviation: string;
      const therapy = therapyItems.find(therapyItem => therapyItem.id === integratedCare.therapyId);
      if (therapy) {
        therapyAbbreviation = therapy.abbreviation;
      }

      const medicalOrderIndex = this.getLastIndexByNodeType(_template, 'medicalOrderData');
      const templateCopy = cloneDeep(_template);
      const lastMedicalOrderNode = templateCopy.templateNodes[medicalOrderIndex];
      if (medicalOrderIndex != null) {
        if (serviceOrderIndex === 0) {
          (_template.templateNodes[medicalOrderIndex].children[0] as any)._id = `medicalOrderData${
            serviceOrderIndex + 1
          }`;
          _template.templateNodes[medicalOrderIndex].title = therapyAbbreviation
            ? `Verordnungsanforderung, ${therapyAbbreviation}`
            : 'Verordnungsanforderung';
        } else {
          (lastMedicalOrderNode.children[0] as any)._id = `medicalOrderData${serviceOrderIndex + 1}`;
          lastMedicalOrderNode.title = therapyAbbreviation
            ? `Verordnungsanforderung, ${therapyAbbreviation}`
            : 'Verordnungsanforderung';
          _template.templateNodes.splice(medicalOrderIndex + 1, 0, lastMedicalOrderNode);
        }
      }
    }
    const audit = await this._auditService.createAudit(_template, prefillData);
    audit.patientId = patient._id;
    audit.regionId = patient.regionId;
    audit.entityType = 'medicalOrder';
    if (previousMedicalOrderRequest && previousMedicalOrderRequest.additionalProperties) {
      this.prefillAuditFromAdditionalProperties(audit.auditNodes, previousMedicalOrderRequest.additionalProperties);
    }
    return audit;
  }

  public async prepareSingleOrderAudit(
    template: ITemplate,
    patient: IPatient,
    articleData: SwodocArticle[],
    singleOrder: ISingleOrder
  ): Promise<IAudit> {
    const prefillData =
      (await this._auditService.getPrefillData(
        template._id,
        patient._id,
        null,
        await this._auth.getCurrentUserId(),
        null,
        null,
        singleOrder
      )) || [];

    // for the article node in the document we need to load the article data
    if (articleData?.length > 0) {
      prefillData.push({ _id: 'singleOrderArticleData', value: articleData });
    }
    const audit = this.getAudit(template, patient, prefillData, IntegratedCareDocumentEntity.singleOrder);
    return audit;
  }

  public async preparePrescriptionAudit(
    template: ITemplate,
    { patient, selectedSingleOrders }: { patient: IPatient; selectedSingleOrders: ISingleOrder[] },
    previousPrescriptionRequest?: IPrescriptionRequest
  ): Promise<IAudit> {
    const _template = cloneDeep(template);
    let metaData: IPrepareAuditMetaDataInterface;
    if (selectedSingleOrders.length === 1) {
      let bodyPart: BodyPart;
      if (selectedSingleOrders[0].location) {
        bodyPart = this._bodyPartService.bodyParts.find(bp => bp.areaId === selectedSingleOrders[0].location);
      }
      metaData = {
        selectedTherapy: selectedSingleOrders[0].therapyId,
        selectedTherapyType: selectedSingleOrders[0].therapyTypeId,
        selectedWoundLocation: bodyPart,
        patient: null,
        additionalInfo: null,
      };
    }

    const swodocPrefillData = await this._auditService.getPrefillData(
      _template._id,
      patient._id,
      metaData,
      await this._auth.getCurrentUserId()
    );
    const prefillData = swodocPrefillData || [];

    // tslint:disable-next-line: no-shadowed-variable
    for (const { index: singleOrderIndex, singleOrder } of selectedSingleOrders.map((singleOrder, index) => ({
      index,
      singleOrder,
    }))) {
      const integratedCare = await this._queryService.get<IIntegratedCare>(
        singleOrder.integratedCareId,
        IntegratedCareDB
      );
      const doctor = await this._queryService.get<IDoctor>(integratedCare.doctorId, DoctorDB);
      const { diagnose } = integratedCare;

      let correspondenceType;
      let to;
      if (doctor) {
        correspondenceType = await this._institutionAttributeService.getCorrespondenceTypeForInstitutionId(
          integratedCare.doctorId
        );
        to =
          correspondenceType === 'Fax'
            ? (doctor.faxPrescriptionRequest ?? doctor.fax)
            : (doctor.emailPrescriptionRequest ?? doctor.email);
      }
      const startDate = singleOrder.startDate;
      const endDate = singleOrder.endDate;
      const articleLines = [];
      const notAvailableArticleLines = [];
      for (const orderedArticle of singleOrder.orderedArticleLines) {
        const orderedArticleViewModel = orderedArticle as SingleOrderedArticleLineViewModel;
        orderedArticleViewModel.article = await this._queryService.get<IArticle>(orderedArticle.articleId, ArticleDB);

        const articleForPrescription = { ...orderedArticleViewModel, isPrivateSale: orderedArticle.isPrivateSale };
        if (
          articleForPrescription.article.status === ArticleStatus.NotAvailable ||
          articleForPrescription.article.prescriptionType == null ||
          articleForPrescription.isPrivateSale
        ) {
          notAvailableArticleLines.push(articleForPrescription);
        } else {
          articleLines.push(articleForPrescription);
        }
      }

      const prescriptionData = {
        therapyId: integratedCare.therapyId,
        therapyTypeId: integratedCare.therapyTypeId,
        doctorId: integratedCare.doctorId,
        correspondenceType,
        articleLines,
        notAvailableArticleLines,
        singleOrderId: singleOrder._id,
        integratedCareId: singleOrder.integratedCareId,
        templateId: _template._id,
        diagnose,
        to,
        startDate,
        endDate,
        patientId: patient._id,
        payerId: patient.payer?.payerId,
        numberOfInsured: patient.payer?.numberOfInsured,
      };
      prefillData.push({
        _id: `prescriptionData${singleOrderIndex + 1}`,
        value: prescriptionData,
      });
      let therapyAbbreviation: string;
      const therapy = therapyItems.find(therapyItem => therapyItem.id === integratedCare.therapyId);
      if (therapy) {
        therapyAbbreviation = therapy.abbreviation;
      }

      const prescriptionIndex = this.getLastIndexByNodeType(_template, 'prescriptionData');
      const templateCopy = cloneDeep(_template);
      const lastPrescriptionNode = templateCopy.templateNodes[prescriptionIndex];
      if (prescriptionIndex != null) {
        if (singleOrderIndex === 0) {
          (_template.templateNodes[prescriptionIndex].children[0] as any)._id = `prescriptionData${
            singleOrderIndex + 1
          }`;
          _template.templateNodes[prescriptionIndex].title = therapyAbbreviation
            ? `Rezeptanforderung, ${therapyAbbreviation}`
            : 'Rezeptanforderung';
        } else {
          (lastPrescriptionNode.children[0] as any)._id = `prescriptionData${singleOrderIndex + 1}`;
          lastPrescriptionNode.title = therapyAbbreviation
            ? `Rezeptanforderung, ${therapyAbbreviation}`
            : 'Rezeptanforderung';
          _template.templateNodes.splice(prescriptionIndex + 1, 0, lastPrescriptionNode);
        }
      }
    }
    const audit = await this._auditService.createAudit(_template, prefillData);

    audit.patientId = patient._id;
    audit.regionId = patient.regionId;
    audit.entityType = 'prescription';
    // Prefill data from oldPrescriptionRequest if available
    if (previousPrescriptionRequest && previousPrescriptionRequest.additionalProperties) {
      this.prefillAuditFromAdditionalProperties(audit.auditNodes, previousPrescriptionRequest.additionalProperties);
    }
    return audit;
  }

  /**
   * Recursively prefills audit nodes with data from additionalProperties
   * @param auditNodes The audit nodes to prefill
   * @param additionalProperties The additional properties containing the data
   */
  private prefillAuditFromAdditionalProperties(auditNodes: any[], additionalProperties: any): void {
    if (!auditNodes || !additionalProperties) {
      return;
    }

    // Process each audit node
    for (const node of auditNodes) {
      // If this is a section node, find matching section in additionalProperties
      if (node.nodeType_Id === 'section' && node.title) {
        const sectionData = additionalProperties[node.title];

        if (sectionData) {
          // Process child nodes with the section data
          if (node.children && node.children.length > 0) {
            this.prefillNodeChildrenFromSectionData(node.children, sectionData);
          }

          // If the section itself has nested sections, recursively process them
          if (node.children) {
            // Find any child nodes that are sections
            const childSections = node.children.filter(child => child.nodeType_Id === 'section');
            if (childSections.length > 0) {
              this.prefillAuditFromAdditionalProperties(childSections, additionalProperties);
            }
          }
        }
      }
    }
  }

  /**
   * Prefills node children with data from a section
   * @param nodeChildren The children nodes to prefill
   * @param sectionData The section data containing the values
   */
  private prefillNodeChildrenFromSectionData(nodeChildren: any[], sectionData: any): void {
    for (const childNode of nodeChildren) {
      // Check if the node title matches a property in sectionData
      if (childNode.title && sectionData[childNode.title] !== undefined) {
        // Prefill the node's response with the value from additionalProperties
        const responseValue = sectionData[childNode.title];

        // Skip if the value is empty or null
        if (responseValue === '' || responseValue === null) continue;
        // Also skip if a value is already set. e.g. by matches
        if (childNode.responses && childNode.responses.length > 0) continue;
        // Create a response object based on the node type
        switch (childNode.nodeType_Id) {
          case 'text':
          case 'textarea':
            childNode.responses = [
              {
                value: responseValue,
              },
            ];
            break;
          // Add more cases for other node types if needed
          case 'multiplechoice':
            // For multiplechoice, we need to set both responses and selectables
            // The responseValue is expected to be a string with line breaks separating the selected options
            if (typeof responseValue === 'string') {
              // Set the response value as is
              childNode.responses = [
                {
                  value: responseValue,
                },
              ];

              // If the node has selectables, mark the ones that are in the responseValue as selected
              if (childNode.selectables && childNode.selectables.length > 0) {
                const selectedValues = responseValue.split('\n').filter(val => val.trim() !== '');

                // Update each selectable's selected state based on whether its label is in the selectedValues
                childNode.selectables = childNode.selectables.map(selectable => ({
                  ...selectable,
                  selected: selectedValues.includes(selectable.label),
                }));
              }
            }
            break;
          case 'checkbox':
            // For checkbox, we set the selectable's selected property based on the boolean value
            if (typeof responseValue === 'boolean') {
              childNode.responses = [
                {
                  value: responseValue,
                },
              ];
              childNode.selectables = [
                {
                  label: '',
                  value: '',
                  selected: responseValue,
                },
              ];
              // If the node has selectables, set the first one's selected state to the responseValue
            }
            break;
          default:
            // For other node types, only set if it's a simple value
            if (
              typeof responseValue === 'string' ||
              typeof responseValue === 'number' ||
              typeof responseValue === 'boolean'
            ) {
              childNode.responses = [
                {
                  value: responseValue as any,
                },
              ];
            }
            break;
        }
      }

      // If this node has children (could be a nested structure), recursively process them
      if (childNode.children && childNode.children.length > 0 && childNode.nodeType_Id === 'section') {
        // If this is a section with a matching section in additionalProperties
        if (childNode.title && sectionData[childNode.title]) {
          this.prefillNodeChildrenFromSectionData(childNode.children, sectionData[childNode.title]);
        }
      }
    }
  }

  private getLastIndexByNodeType(template: ITemplate, nodeType: string): number {
    let lastIndex: number;
    template.templateNodes.forEach((templateNode, index) => {
      templateNode.children.forEach(child => {
        if (child.nodeType_Id.startsWith(nodeType)) {
          lastIndex = index;
        }
      });
    });
    return lastIndex;
  }
}
